import { db } from '../../config/firebase';
import {
  collection,
  doc,
  getDoc,
  getDocs,
  setDoc,
  updateDoc,
  query,
  where,
  orderBy,
  Timestamp,
  onSnapshot,
  Unsubscribe
} from 'firebase/firestore';
import { AuditService } from './AuditService';
import { HomeworkAssignment } from '../../types/client';

export class FirebaseHomeworkService {
  private static COLLECTION = 'homework';

  /**
   * Assign homework to a client (provider only).
   */
  static async assignHomework(
    data: Omit<HomeworkAssignment, 'id' | 'createdAt' | 'updatedAt'>
  ): Promise<string> {
    try {
      const docRef = doc(collection(db, this.COLLECTION));
      await setDoc(docRef, {
        ...data,
        status: data.status || 'pending',
        createdAt: Timestamp.now(),
        updatedAt: Timestamp.now(),
      });
      AuditService.logPhiModification(
        data.providerId, 'provider', 'create', 'form' as any, docRef.id, data.clientId,
        `Assigned homework: ${data.title}`
      );
      return docRef.id;
    } catch (error) {
      console.error('Error assigning homework:', error);
      throw error;
    }
  }

  /**
   * Get all homework for a client, ordered by most recent.
   */
  static async getHomeworkByClient(clientId: string, providerId?: string): Promise<HomeworkAssignment[]> {
    try {
      let q;
      if (providerId) {
        q = query(
          collection(db, this.COLLECTION),
          where('clientId', '==', clientId),
          where('providerId', '==', providerId),
          orderBy('createdAt', 'desc')
        );
      } else {
        q = query(
          collection(db, this.COLLECTION),
          where('clientId', '==', clientId),
          orderBy('createdAt', 'desc')
        );
      }
      const snapshot = await getDocs(q);
      return snapshot.docs.map(d => this.fromFirestore(d.id, d.data()));
    } catch (error) {
      console.error('Error fetching client homework:', error);
      return [];
    }
  }

  /**
   * Get all homework assigned by a provider for a specific client.
   */
  static async getHomeworkByProvider(providerId: string, clientId: string): Promise<HomeworkAssignment[]> {
    try {
      const q = query(
        collection(db, this.COLLECTION),
        where('clientId', '==', clientId),
        where('providerId', '==', providerId),
        orderBy('createdAt', 'desc')
      );
      const snapshot = await getDocs(q);
      return snapshot.docs.map(d => this.fromFirestore(d.id, d.data()));
    } catch (error) {
      console.error('Error fetching provider homework:', error);
      return [];
    }
  }

  /**
   * Get a single homework assignment by ID.
   */
  static async getHomework(homeworkId: string): Promise<HomeworkAssignment | null> {
    try {
      const docSnap = await getDoc(doc(db, this.COLLECTION, homeworkId));
      if (docSnap.exists()) {
        return this.fromFirestore(docSnap.id, docSnap.data());
      }
      return null;
    } catch (error) {
      console.error('Error fetching homework:', error);
      return null;
    }
  }

  /**
   * Update homework status (client marks complete, or provider updates).
   */
  static async updateHomeworkStatus(
    homeworkId: string,
    status: HomeworkAssignment['status'],
    userId: string,
    userRole: 'provider' | 'client',
    clientNotes?: string
  ): Promise<void> {
    try {
      const existing = await this.getHomework(homeworkId);
      if (!existing) throw new Error('Homework not found');

      const updates: Record<string, any> = {
        status,
        updatedAt: Timestamp.now(),
      };

      if (status === 'completed') {
        updates.completedAt = Timestamp.now();
        updates.completedBy = userId;
      }

      if (clientNotes !== undefined) {
        updates.clientNotes = clientNotes;
      }

      await updateDoc(doc(db, this.COLLECTION, homeworkId), updates);

      AuditService.logPhiModification(
        userId, userRole, 'update', 'form' as any, homeworkId, existing.clientId,
        `Homework ${status}: ${existing.title}`
      );
    } catch (error) {
      console.error('Error updating homework status:', error);
      throw error;
    }
  }

  /**
   * Update homework details (provider only).
   */
  static async updateHomework(
    homeworkId: string,
    updates: Partial<Pick<HomeworkAssignment, 'title' | 'description' | 'dueDate'>>,
    providerId: string
  ): Promise<void> {
    try {
      const existing = await this.getHomework(homeworkId);
      if (!existing) throw new Error('Homework not found');

      await updateDoc(doc(db, this.COLLECTION, homeworkId), {
        ...updates,
        updatedAt: Timestamp.now(),
      });

      AuditService.logPhiModification(
        providerId, 'provider', 'update', 'form' as any, homeworkId, existing.clientId,
        `Updated homework: ${existing.title}`
      );
    } catch (error) {
      console.error('Error updating homework:', error);
      throw error;
    }
  }

  /**
   * Subscribe to real-time homework updates for a client.
   */
  static subscribeToClientHomework(
    clientId: string,
    callback: (homework: HomeworkAssignment[]) => void
  ): Unsubscribe {
    const q = query(
      collection(db, this.COLLECTION),
      where('clientId', '==', clientId),
      orderBy('createdAt', 'desc')
    );
    return onSnapshot(q, (snapshot) => {
      callback(snapshot.docs.map(d => this.fromFirestore(d.id, d.data())));
    }, (error) => {
      console.error('Error subscribing to client homework:', error);
    });
  }

  /**
   * Subscribe to real-time homework updates for a provider's client.
   */
  static subscribeToProviderHomework(
    providerId: string,
    clientId: string,
    callback: (homework: HomeworkAssignment[]) => void
  ): Unsubscribe {
    const q = query(
      collection(db, this.COLLECTION),
      where('clientId', '==', clientId),
      orderBy('createdAt', 'desc')
    );
    return onSnapshot(q, (snapshot) => {
      callback(snapshot.docs.map(d => this.fromFirestore(d.id, d.data())));
    }, (error) => {
      console.error('Error subscribing to provider homework:', error);
    });
  }

  private static fromFirestore(id: string, data: any): HomeworkAssignment {
    return {
      id,
      title: data.title || '',
      description: data.description || '',
      dueDate: data.dueDate || '',
      assignedBy: data.assignedBy || '',
      status: data.status || 'pending',
      attachments: data.attachments || [],
      clientId: data.clientId || '',
      providerId: data.providerId || '',
      createdAt: data.createdAt?.toDate() || new Date(),
      updatedAt: data.updatedAt?.toDate() || new Date(),
      completedAt: data.completedAt?.toDate(),
      completedBy: data.completedBy,
      clientNotes: data.clientNotes,
    };
  }
}
